import { useState, useEffect } from "react"
import { useStaticQuery, graphql } from "gatsby"

const BEST_SELLERS_SORT_NAME = "Best Sellers"
const MAX_TRENDING_FACETS = 10
// Over-fetch so excluded collections don't shrink the displayed list below MAX_TRENDING_FACETS
const MAX_FETCHED_FACETS = 30

const useAlgoliaTrendingFacets = (facetName: string) => {
  const [facetValues, setFacetValues] = useState<string[]>([])
  const [loading, setLoading] = useState(true)

  const { config } = useStaticQuery<GatsbyTypes.StaticAlgoliaTrendingFacetsQuery>(graphql`
    query StaticAlgoliaTrendingFacets {
      config: sanitySettingAlgolia {
        applicationId
        searchOnlyApiKey
        trendingExcludedCollections
        sortOptions {
          name
          handle
        }
      }
    }
  `)

  useEffect(() => {
    const appId = config?.applicationId
    const apiKey = config?.searchOnlyApiKey
    const indexName = config?.sortOptions?.find((o: any) => o.name === BEST_SELLERS_SORT_NAME)?.handle

    if (!appId || !apiKey || !indexName) {
      setLoading(false)
      return
    }

    let cancelled = false

    const run = async () => {
      try {
        const res = await fetch(`https://${appId}.algolia.net/1/indexes/*/recommendations`, {
          method: "POST",
          headers: {
            "X-Algolia-Application-Id": appId,
            "X-Algolia-API-Key": apiKey,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            requests: [
              {
                indexName,
                model: "trending-facets",
                facetName,
                threshold: 0,
                maxRecommendations: MAX_FETCHED_FACETS,
              },
            ],
          }),
        })

        if (!res.ok) {
          const body = await res.json().catch(() => null)
          throw new Error(`[TrendingFacets] Failed: ${res.status} ${JSON.stringify(body)}`)
        }

        const hits = (await res.json()).results?.[0]?.hits ?? []
        const excludedHandles = new Set(
          (config?.trendingExcludedCollections ?? [])
            .filter((handle): handle is string => Boolean(handle))
            .map(handle => handle.trim().toLowerCase())
        )
        const values = (hits.map((h: any) => h.facetValue).filter(Boolean) as string[])
          .filter(value => !excludedHandles.has(value.trim().toLowerCase()))
          .slice(0, MAX_TRENDING_FACETS)

        if (!cancelled) setFacetValues(values)
      } catch (error) {
        console.error("[TrendingFacets] Error fetching trending facet values:", error)
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    run()

    return () => {
      cancelled = true
    }
  }, [config, facetName]) // eslint-disable-line react-hooks/exhaustive-deps

  return { facetValues, loading }
}

export { useAlgoliaTrendingFacets }
